import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, MapPin, Briefcase } from "lucide-react";
import { api, errMsg, fmtDate } from "../../lib/api";
import { PageHeader, BeltBadge, StatusPill, Empty } from "../../components/common";
import { CategorySelect, SectorSelect } from "../../components/JobCategories";

export default function JobBoard() {
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [category, setCategory] = useState("");
  const [sector, setSector] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);

  const load = () => Promise.all([api.get("/jobs", { params: { q, location: loc, category, sector, country } }), api.get("/my/applications")]).then(([j, a]) => { setJobs(j.data); setApps(a.data); });
  useEffect(() => { load(); }, [category, sector, country]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/jobs/countries").then((r) => setCountries(r.data)); }, []);

  const apply = async (id) => {
    try { await api.post(`/jobs/${id}/apply`); toast.success("Application sent"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const appFor = (id) => apps.find((a) => a.job_id === id);

  return (
    <div>
      <PageHeader eyebrow="Jobs" title="Find work" subtitle="Apply directly, or get matched automatically when employers search for your skills." />
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="dx-card p-3 flex flex-col sm:flex-row gap-2 mb-6">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-3 text-[#8a7a92]" /><input className="dx-input pl-9 border-0" placeholder="Job title, skill or company" value={q} onChange={(e) => setQ(e.target.value)} data-testid="jobs-search-input" /></div>
        <div className="relative sm:w-56"><MapPin className="w-4 h-4 absolute left-3 top-3 text-[#8a7a92]" /><input className="dx-input pl-9 border-0" placeholder="City or town" value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="jobs-location-input" /></div>
        <button className="dx-btn-purple" data-testid="jobs-search-btn">Search</button>
      </form>
      <div className="flex flex-col sm:flex-row gap-2 mb-6 -mt-3">
        <select className="dx-input sm:w-52" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="jobs-country-select">
          <option value="">All countries</option>
          {countries.map((c) => <option key={c}>{c}</option>)}
        </select>
        <CategorySelect value={category} onChange={setCategory} allLabel="All job categories" testid="jobs-category-select" className="dx-input sm:w-80" />
        <SectorSelect value={sector} onChange={setSector} allLabel="All sectors" testid="jobs-sector-select" className="dx-input sm:w-56" />
        <span className="text-xs text-[#8a7a92] self-center" data-testid="jobs-count">{jobs.length} open jobs</span>
      </div>
      {jobs.length === 0 ? <Empty icon={Briefcase} title="No open jobs found" text="Try another search, or keep training - employers can match you directly." testid="jobs-empty" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {jobs.map((j) => {
            const a = appFor(j.id);
            return (
              <div key={j.id} className="dx-card p-6" data-testid={`job-card-${j.id}`}>
                <div className="flex justify-between gap-3">
                  <div>
                    <h3 className="font-display font-semibold text-lg text-[#250E31]">{j.title}</h3>
                    <div className="text-sm text-[#6b5a73]">{j.company_name} · {j.location}{j.country ? `, ${j.country}` : ""} · {j.job_type}</div>
                    {j.category && <div className="text-xs font-semibold text-[#5B2A6E] mt-1">{j.category} · {j.sector}</div>}
                  </div>
                  {j.min_belt !== "none" && <BeltBadge belt={j.min_belt} />}
                </div>
                {j.description && <p className="text-sm text-[#4b3d52] mt-3 line-clamp-3">{j.description}</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">{j.skills.map((s) => <span key={s} className="text-xs rounded-full bg-[#F4EEF7] text-[#5B2A6E] px-2.5 py-1">{s}</span>)}</div>
                <div className="flex items-center justify-between mt-5">
                  <span className="text-xs text-[#8a7a92]">{j.salary_range || "Salary on request"} · {fmtDate(j.created_at)}{j.source_url && <> · <a href={j.source_url} target="_blank" rel="noreferrer" className="text-[#5B2A6E] underline" data-testid={`job-${j.id}-source-link`}>Original advert</a></>}</span>
                  {a ? <StatusPill status={a.status} testid={`job-${j.id}-status`} /> : <button className="dx-btn-gold" onClick={() => apply(j.id)} data-testid={`job-${j.id}-apply-btn`}>Apply</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
